/**
 * Presentation helpers for aircraft telemetry.
 *
 * Shared by the map popup, the device list and the HUD overlay so an operator
 * reads the same number the same way wherever it appears. Every formatter takes
 * the loose `number | null | undefined` the SSE payload actually delivers and
 * returns null when there is nothing to show, so callers can omit a row rather
 * than print "N/A" or, worse, "0".
 *
 * Altitude carries both units: the telemetry is metric because that is what MSDK
 * reports, but crews brief height in feet.
 */

const CARDINALS = [
  'N', 'NNE', 'NE', 'ENE',
  'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW',
  'W', 'WNW', 'NW', 'NNW',
];

const METRES_PER_FOOT = 0.3048;
const METRES_PER_MILE = 1609.344;

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Wrap any angle into 0..360. */
export function normaliseBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Sixteen-point compass label. 348.75deg and above wraps back to N. */
export function cardinalFromDeg(deg: number): string {
  const idx = Math.round(normaliseBearing(deg) / 22.5) % 16;
  return CARDINALS[idx];
}

/** Course as degrees plus compass point, e.g. "148° SE". */
export function formatBearing(deg?: number | null): string | null {
  if (!isNum(deg)) return null;
  const d = normaliseBearing(deg);
  return `${Math.round(d)}° ${cardinalFromDeg(d)}`;
}

/** Compass point alone, for tight spaces like the device list. */
export function formatCardinal(deg?: number | null): string | null {
  if (!isNum(deg)) return null;
  return cardinalFromDeg(deg);
}

/**
 * Height above the takeoff point in both units.
 *
 * Deliberately not labelled MSL anywhere: MSDK reports height relative to where
 * the aircraft took off, which is all the footprint projection assumes too.
 */
export function formatAltitudeAgl(metres?: number | null): string | null {
  if (!isNum(metres)) return null;
  const feet = Math.round(metres / METRES_PER_FOOT);
  return `${Math.round(metres)} m (${feet} ft)`;
}

/** Signed climb rate. The sign is the point, so it is always shown. */
export function formatVerticalSpeed(ms?: number | null): string | null {
  if (!isNum(ms)) return null;
  const sign = ms > 0 ? '+' : ms < 0 ? '−' : '';
  return `${sign}${Math.abs(ms).toFixed(1)} m/s`;
}

/** Metres under a kilometre, then kilometres with miles alongside. */
export function formatDistance(metres?: number | null): string | null {
  if (!isNum(metres) || metres < 0) return null;
  if (metres < 1000) return `${Math.round(metres)} m`;
  const miles = metres / METRES_PER_MILE;
  return `${(metres / 1000).toFixed(2)} km (${miles.toFixed(2)} mi)`;
}

/** Ground area in acres, matching the coverage endpoint's unit. */
export function formatArea(squareMetres?: number | null): string | null {
  if (!isNum(squareMetres) || squareMetres <= 0) return null;
  const acres = squareMetres / 4046.8564224;
  if (acres < 10) return `${acres.toFixed(2)} ac`;
  return `${acres.toFixed(1)} ac`;
}

/** Lens field of view as horizontal by vertical. */
export function formatFov(
  hfovDeg?: number | null,
  vfovDeg?: number | null
): string | null {
  if (!isNum(hfovDeg) || !isNum(vfovDeg)) return null;
  return `${hfovDeg.toFixed(0)}° × ${vfovDeg.toFixed(0)}°`;
}

/**
 * Gimbal pitch, where 0 is horizontal and -90 is straight down. Phrased as a
 * depression angle because "-90°" alone reads as an error to anyone who has not
 * met the convention.
 */
export function formatGimbalPitch(deg?: number | null): string | null {
  if (!isNum(deg)) return null;
  const rounded = Math.round(deg);
  if (rounded <= -89) return `${rounded}° (nadir)`;
  if (rounded >= -1 && rounded <= 1) return `${rounded}° (level)`;
  return `${rounded}°`;
}

/** SDK constant names like YAW_FOLLOW are shouted at the operator otherwise. */
export function formatGimbalMode(mode?: string | null): string | null {
  if (!mode) return null;
  const words = mode.replace(/_/g, ' ').toLowerCase().trim();
  if (!words) return null;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Great-circle distance in metres. Used for distance from the home point, which
 * the aircraft reports as a coordinate rather than a range.
 */
export function distanceMetres(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLon = (lon2 - lon1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Range from the takeoff point, or null when the home fix is not known yet. */
export function formatDistanceFromHome(device: {
  latitude?: number | null;
  longitude?: number | null;
  home_latitude?: number | null;
  home_longitude?: number | null;
}): string | null {
  const { latitude, longitude, home_latitude, home_longitude } = device;
  if (
    !isNum(latitude) || !isNum(longitude) ||
    !isNum(home_latitude) || !isNum(home_longitude)
  ) {
    return null;
  }
  return formatDistance(
    distanceMetres(home_latitude, home_longitude, latitude, longitude)
  );
}

/**
 * GPS quality as the pilot judges it. DJI treats a handful of satellites as
 * unusable for anything but attitude hold, which is why the count is worth
 * showing rather than a bare fix/no-fix.
 */
export function satelliteQuality(count?: number | null): 'good' | 'fair' | 'poor' | null {
  if (!isNum(count)) return null;
  if (count >= 12) return 'good';
  if (count >= 7) return 'fair';
  return 'poor';
}
